import { JWT } from 'next-auth/jwt';

import { EnvironmentVariable, getEnvVar } from '@lib/config';
import { failingFetcher } from '@lib/http/fetch';
import { Maybe } from '@lib/types/Maybe';
import { Sale } from '@lib/types/Sale';
import { Asset } from '@lib/types/api/Asset';
import {
  CheckEntitlementRequest,
  CheckEntitlementResponse,
  ClaimUpmEntitlementRequest,
  ClaimUpmEntitlementResponse,
  SearchProductEntitlementsRequest,
  SearchProductsEntitlementsResponse,
} from '@lib/types/api/Entitlement';
import { Organization } from '@lib/types/api/Organization';
import {
  GetPackageVersionsRequest,
  GetPackageVersionsResponse,
} from '@lib/types/api/Package';
import { GetProductRequest, GetProductResponse } from '@lib/types/api/Product';
import {
  AddProductListItemV2Request,
  CreateProductListV2Request,
  GetProductListItemsV2Params,
  GetProductListItemsV2Response,
  GetProductListsV2Params,
  GetProductListsV2Response,
  LookupProductListsResponse,
  ProductListItemV2,
  ProductListV2,
  UpdateProductListV2Request,
} from '@lib/types/api/ProductListV2';
import {
  GetProductPreviewRequest,
  GetProductPreviewResponse,
} from '@lib/types/api/ProductPreview';
import { GetPublisherRequest, Publisher } from '@lib/types/api/Publisher';
import {
  GetPublisherListsRequest,
  GetPublisherListsResponse,
} from '@lib/types/api/PublisherList';
import {
  CreateReviewRequest,
  DeleteReviewResponse,
  GetProductReviewOptions,
  GetProductReviewResponse,
  GetProductReviewStatisticsResponse,
  GetProductReviewStatisticsV2Response,
  GetReportReviewResponse,
  GetReviewActionsRequest,
  GetReviewActionsResponse,
  GetReviewResponse,
  ListReviewsOptions,
  ListReviewsResponse,
  Review,
  UpdateReviewRequest,
  UpdateReviewResponse,
} from '@lib/types/api/Review';
import { GetUserPublicListsResponse, UserList } from '@lib/types/api/UserList';
import { UserProfile } from '@lib/types/api/UserProfile';
import { UserDetails } from '@lib/types/api/user/UserDetails';
import { buildQueryString } from '@lib/util/queryString';
import { trace } from '@opentelemetry/api';
import { encode } from 'base-64';
import { decodeHtmlEntities } from 'utils';

export type ClientOptions = {
  baseUrl?: string;
  dynamicBackend?: string;
  jwt?: Maybe<JWT>;
};

const SPAN_PADDING = 2;
const SPAN_PAD_CHAR = '0';

const REVALIDATE_ONE_HOUR_IN_SECONDS = 3600;

type GetHeadersOptions = {
  dynamicBackend?: string;
  useServiceAccount?: boolean;
  jwt?: Maybe<JWT>;
};

export type GetLoggedInUsersOrganizationsOptions = {
  /**
   * Include publisher organizations, which the endpoint excludes by default.
   * Only the product-list paths pass this; purchase, entitlement and
   * org-switcher paths must keep the default.
   */
  includePublisherOrgs?: boolean;
};

const getHeaders = (options: GetHeadersOptions) => {
  const headers = {};

  if (options.dynamicBackend) {
    headers['x-backend-host'] = options.dynamicBackend;
  }

  if (options.jwt && options.jwt['unity_token']) {
    headers['Authorization'] = `Bearer ${options.jwt['unity_token']}`;
  } else if (options.useServiceAccount) {
    const username = getEnvVar(EnvironmentVariable.usgApiUsername);
    const password = getEnvVar(EnvironmentVariable.usgApiPassword);
    const credentials = `${username}:${password}`;
    headers['Authorization'] = `Basic ${encode(credentials)}`;
  }

  const activeSpan = trace.getActiveSpan();
  if (activeSpan) {
    const spanContext = activeSpan.spanContext();
    headers['traceparent'] = `00-${spanContext.traceId}-${
      spanContext.spanId
    }-${spanContext.traceFlags
      .toString()
      .padStart(SPAN_PADDING, SPAN_PAD_CHAR)}`;
  }

  return headers;
};

const decodeProductList = (list: ProductListV2): ProductListV2 => ({
  ...list,
  ...(typeof list.name === 'string' && {
    name: decodeHtmlEntities(list.name),
  }),
  ...(typeof list.description === 'string' && {
    description: decodeHtmlEntities(list.description),
  }),
});

export function createMarketplaceClient(options?: ClientOptions) {
  const jwt = options?.jwt;
  const genesisUser = jwt ? (jwt['genesis_user'] as string) : '';
  const baseUrl =
    options?.baseUrl ?? `${getEnvVar(EnvironmentVariable.usgApi)}/marketplace`;
  const dynamicBackend =
    options?.dynamicBackend ?? getEnvVar(EnvironmentVariable.dynamicBackendApi);

  const getAssets = async () => {
    return failingFetcher(`${baseUrl}/assets/v1`, {
      headers: {
        ...getHeaders({
          dynamicBackend,
          useServiceAccount: true,
          jwt,
        }),
      },
    });
  };

  const getUserAccount = async (): Promise<UserDetails> => {
    return failingFetcher(
      `${baseUrl}/users/v1/${encodeURIComponent(genesisUser)}/`,
      {
        headers: {
          ...getHeaders({
            dynamicBackend,
            useServiceAccount: true,
          }),
        },
      }
    );
  };

  const getNotifications = async (
    locale: string,
    page: number,
    rows: number
  ) => {
    return failingFetcher(
      `${baseUrl}/notifications/v1/users/${encodeURIComponent(genesisUser)}?locale=${locale}&page=${page}&rows=${rows}`,
      {
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const getUnreadNotificationsCount = async (locale: string) => {
    return failingFetcher(
      `${baseUrl}/notifications/v1/users/${encodeURIComponent(genesisUser)}/unread-count?locale=${locale}`,
      {
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const markNotificationAsRead = async (
    locale: string,
    notificationId: string
  ) => {
    return failingFetcher(
      `${baseUrl}/notifications/v1/${encodeURIComponent(notificationId)}/users/${encodeURIComponent(genesisUser)}?locale=${locale}`,
      {
        method: 'PATCH',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const markAllNotificationAsRead = async (
    locale: string,
    notificationIds: string
  ) => {
    return failingFetcher(
      `${baseUrl}/notifications/v1/users/${encodeURIComponent(genesisUser)}?locale=${locale}&notificationIds=${notificationIds}`,
      {
        method: 'PATCH',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const getUserProfile = async (userId: string): Promise<UserProfile> => {
    return failingFetcher(`${baseUrl}/users/v1/${encodeURIComponent(userId)}`, {
      headers: {
        ...getHeaders({
          dynamicBackend,
          useServiceAccount: true,
        }),
      },
    });
  };

  const getUserPublicLists = async (
    userId: string,
    start: number,
    count: number
  ): Promise<UserList[]> => {
    const response: GetUserPublicListsResponse = await failingFetcher(
      `${baseUrl}/productlist/v1/users/${encodeURIComponent(userId)}?start=${start}&count=${count}`,
      {
        headers: {
          ...getHeaders({
            dynamicBackend,
            useServiceAccount: true,
          }),
        },
      }
    );

    return response.results;
  };

  const getUserLists = async (
    params?: GetProductListsV2Params
  ): Promise<GetProductListsV2Response> => {
    const queryString = buildQueryString(params ?? {});
    const lists: GetProductListsV2Response = await failingFetcher(
      `${baseUrl}/productlist/v2/users/${encodeURIComponent(genesisUser)}/lists${queryString ? `?${queryString}` : ''}`,
      {
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
    return Array.isArray(lists) ? lists.map(decodeProductList) : lists;
  };

  const getUserList = async (listId: string): Promise<ProductListV2> => {
    const list: ProductListV2 = await failingFetcher(
      `${baseUrl}/productlist/v2/users/${encodeURIComponent(genesisUser)}/lists/${encodeURIComponent(listId)}`,
      {
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
    return decodeProductList(list);
  };

  const getUserListItems = async (
    listId: string,
    params?: GetProductListItemsV2Params
  ): Promise<GetProductListItemsV2Response> => {
    const queryString = buildQueryString(params ?? {});
    return failingFetcher(
      `${baseUrl}/productlist/v2/users/${encodeURIComponent(genesisUser)}/lists/${encodeURIComponent(listId)}/items${queryString ? `?${queryString}` : ''}`,
      {
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const createUserList = async (
    data: CreateProductListV2Request
  ): Promise<ProductListV2> => {
    const list: ProductListV2 = await failingFetcher(
      `${baseUrl}/productlist/v2/users/${encodeURIComponent(genesisUser)}/lists`,
      {
        method: 'POST',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
        body: JSON.stringify(data),
      }
    );
    return decodeProductList(list);
  };

  const updateUserList = async (
    listId: string,
    data: UpdateProductListV2Request
  ): Promise<ProductListV2> => {
    const list: ProductListV2 = await failingFetcher(
      `${baseUrl}/productlist/v2/users/${encodeURIComponent(genesisUser)}/lists/${encodeURIComponent(listId)}`,
      {
        method: 'PATCH',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
        body: JSON.stringify(data),
      }
    );
    return decodeProductList(list);
  };

  const deleteUserList = async (listId: string): Promise<void> => {
    return failingFetcher(
      `${baseUrl}/productlist/v2/users/${encodeURIComponent(genesisUser)}/lists/${encodeURIComponent(listId)}`,
      {
        method: 'DELETE',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const addItemToUserList = async (
    listId: string,
    data: AddProductListItemV2Request
  ): Promise<ProductListItemV2> => {
    return failingFetcher(
      `${baseUrl}/productlist/v2/users/${encodeURIComponent(genesisUser)}/lists/${encodeURIComponent(listId)}/items`,
      {
        method: 'POST',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
        body: JSON.stringify(data),
      }
    );
  };

  const removeItemFromUserList = async (
    listId: string,
    namespace: string,
    productId: string
  ): Promise<void> => {
    return failingFetcher(
      `${baseUrl}/productlist/v2/users/${encodeURIComponent(genesisUser)}/lists/${encodeURIComponent(listId)}/items/${encodeURIComponent(namespace)}/${encodeURIComponent(productId)}`,
      {
        method: 'DELETE',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const getAsset = async (
    assetId: string,
    currency: string,
    locale: string
  ): Promise<Asset> => {
    return failingFetcher(
      `${baseUrl}/assets/v1/${encodeURIComponent(assetId)}?currency=${currency}&locale=${locale}`,
      {
        headers: {
          ...getHeaders({
            dynamicBackend,
            useServiceAccount: true,
          }),
        },
        cache: 'no-cache',
      }
    );
  };

  const getPublisher = async ({
    publisherId,
  }: GetPublisherRequest): Promise<Publisher> => {
    return failingFetcher(
      `${baseUrl}/publisher/v1/profile/${encodeURIComponent(publisherId)}`,
      {
        headers: {
          ...getHeaders({
            dynamicBackend,
            useServiceAccount: true,
          }),
        },
        cache: 'no-cache',
      }
    );
  };

  const getPublisherLists = async ({
    start,
    count,
    organizationId,
  }: GetPublisherListsRequest): Promise<GetPublisherListsResponse> => {
    const params = {
      start,
      count,
    };

    const queryString = buildQueryString(params);

    return failingFetcher(
      `${baseUrl}/productlist/v1/publishers/${encodeURIComponent(organizationId)}${queryString ? `?${queryString}` : ''}`,
      {
        headers: {
          ...getHeaders({
            dynamicBackend,
            useServiceAccount: true,
          }),
        },
      }
    );
  };

  const getListBySlug = async (slug: string): Promise<ProductListV2> => {
    const list: ProductListV2 = await failingFetcher(
      `${baseUrl}/productlist/v2/lists/${encodeURIComponent(slug)}/slug`,
      {
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
    return decodeProductList(list);
  };

  const getListItemsBySlug = async (
    slug: string,
    params?: GetProductListItemsV2Params
  ): Promise<GetProductListItemsV2Response> => {
    const queryString = buildQueryString(params ?? {});
    return failingFetcher(
      `${baseUrl}/productlist/v2/lists/${encodeURIComponent(slug)}/items${queryString ? `?${queryString}` : ''}`,
      {
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const getOrganizationLists = async (
    organizationId: string,
    params?: GetProductListsV2Params
  ): Promise<GetProductListsV2Response> => {
    const queryString = buildQueryString(params ?? {});
    const lists: GetProductListsV2Response = await failingFetcher(
      `${baseUrl}/productlist/v2/organizations/${encodeURIComponent(organizationId)}/lists${queryString ? `?${queryString}` : ''}`,
      {
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
    return Array.isArray(lists) ? lists.map(decodeProductList) : lists;
  };

  const getOrganizationList = async (
    organizationId: string,
    listId: string
  ): Promise<ProductListV2> => {
    const list: ProductListV2 = await failingFetcher(
      `${baseUrl}/productlist/v2/organizations/${encodeURIComponent(organizationId)}/lists/${encodeURIComponent(listId)}`,
      {
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
    return decodeProductList(list);
  };

  const getOrganizationListItems = async (
    organizationId: string,
    listId: string,
    params?: GetProductListItemsV2Params
  ): Promise<GetProductListItemsV2Response> => {
    const queryString = buildQueryString(params ?? {});
    return failingFetcher(
      `${baseUrl}/productlist/v2/organizations/${encodeURIComponent(organizationId)}/lists/${encodeURIComponent(listId)}/items${queryString ? `?${queryString}` : ''}`,
      {
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const createOrganizationList = async (
    organizationId: string,
    data: CreateProductListV2Request
  ): Promise<ProductListV2> => {
    const list: ProductListV2 = await failingFetcher(
      `${baseUrl}/productlist/v2/organizations/${encodeURIComponent(organizationId)}/lists`,
      {
        method: 'POST',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
        body: JSON.stringify(data),
      }
    );
    return decodeProductList(list);
  };

  const updateOrganizationList = async (
    organizationId: string,
    listId: string,
    data: UpdateProductListV2Request
  ): Promise<ProductListV2> => {
    const list: ProductListV2 = await failingFetcher(
      `${baseUrl}/productlist/v2/organizations/${encodeURIComponent(organizationId)}/lists/${encodeURIComponent(listId)}`,
      {
        method: 'PATCH',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
        body: JSON.stringify(data),
      }
    );
    return decodeProductList(list);
  };

  const deleteOrganizationList = async (
    organizationId: string,
    listId: string
  ): Promise<void> => {
    return failingFetcher(
      `${baseUrl}/productlist/v2/organizations/${encodeURIComponent(organizationId)}/lists/${encodeURIComponent(listId)}`,
      {
        method: 'DELETE',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const addItemToOrganizationList = async (
    organizationId: string,
    listId: string,
    data: AddProductListItemV2Request
  ): Promise<ProductListItemV2> => {
    return failingFetcher(
      `${baseUrl}/productlist/v2/organizations/${encodeURIComponent(organizationId)}/lists/${encodeURIComponent(listId)}/items`,
      {
        method: 'POST',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
        body: JSON.stringify(data),
      }
    );
  };

  const removeItemFromOrganizationList = async (
    organizationId: string,
    listId: string,
    namespace: string,
    productId: string
  ): Promise<void> => {
    return failingFetcher(
      `${baseUrl}/productlist/v2/organizations/${encodeURIComponent(organizationId)}/lists/${encodeURIComponent(listId)}/items/${encodeURIComponent(namespace)}/${encodeURIComponent(productId)}`,
      {
        method: 'DELETE',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const getPublisherByOrganizationId = async (
    organizationId: string
  ): Promise<Publisher> => {
    return failingFetcher(
      `${baseUrl}/publisher/v1/organization/${encodeURIComponent(organizationId)}`,
      {
        headers: {
          ...getHeaders({
            dynamicBackend,
            useServiceAccount: true,
          }),
        },
      }
    );
  };

  const getSalesForAsset = async (
    assetId: string
  ): Promise<{ sales?: Sale[] }> => {
    return failingFetcher(
      `${baseUrl}/sales/v1/products/${encodeURIComponent(assetId)}`,
      {
        headers: {
          ...getHeaders({
            dynamicBackend,
            useServiceAccount: true,
          }),
        },
        cache: 'no-cache',
      }
    );
  };

  const getUserReviews = async (userId: string): Promise<Review[]> => {
    const response = await failingFetcher(
      `${baseUrl}/reviews/v1/users/${encodeURIComponent(userId)}`,
      {
        headers: {
          ...getHeaders({
            dynamicBackend,
            useServiceAccount: true,
          }),
        },
      }
    );

    return response.reviews;
  };

  const getLoggedInUsersOrganizations = async (
    options?: GetLoggedInUsersOrganizationsOptions
  ): Promise<{
    organizations: Organization[];
  }> => {
    const queryString = buildQueryString({
      ...(options?.includePublisherOrgs && { 'include-publisher-orgs': true }),
    });

    return failingFetcher(
      `${baseUrl}/users/v1/${encodeURIComponent(genesisUser)}/organizations${
        queryString ? `?${queryString}` : ''
      }`,
      {
        cache: 'no-store',
        headers: {
          ...getHeaders({
            dynamicBackend,
            useServiceAccount: true,
          }),
        },
      }
    );
  };

  const refreshOrganizationsCache = async (): Promise<void> => {
    return failingFetcher(
      `${baseUrl}/users/v1/me/organizations/cache/refresh`,
      {
        method: 'POST',
        cache: 'no-store',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const getLoggedInUsersCartItems = async () => {
    return failingFetcher(
      `${baseUrl}/carts/v1/users/${encodeURIComponent(genesisUser)}`,
      {
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const getAnonymousUsersCartItem = async (deviceId: string) => {
    return failingFetcher(
      `${baseUrl}/carts/v1/devices/${encodeURIComponent(deviceId)}`,
      {
        headers: {
          ...getHeaders({
            dynamicBackend,
            useServiceAccount: true,
          }),
        },
      }
    );
  };

  const createDevice = async () => {
    return failingFetcher(`${baseUrl}/device/v1`, {
      method: 'POST',
      headers: {
        ...getHeaders({
          dynamicBackend,
          useServiceAccount: true,
        }),
      },
    });
  };

  const getUsersEntitlements = async (page = 1, rows = 100) => {
    return failingFetcher(
      `${baseUrl}/entitlements/v1/users/${encodeURIComponent(genesisUser)}/assetstore?rows=${rows}&page=${page}`,
      {
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const mergeDeviceCartIntoUserCart = async (deviceId?: string) => {
    if (!genesisUser || !deviceId) {
      return null;
    }
    return failingFetcher(
      `${baseUrl}/carts/v1/users/${encodeURIComponent(genesisUser)}/merge/${encodeURIComponent(deviceId)}`,
      {
        method: 'POST',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const postNewsletterSignup = async (email: string, country = 'US') => {
    return failingFetcher(`${baseUrl}/users/v1/newsletter/assetstore`, {
      method: 'POST',
      headers: {
        ...getHeaders({
          dynamicBackend,
          useServiceAccount: true,
        }),
      },
      body: JSON.stringify({ email, country }),
    });
  };

  const getReviewsForProduct = (
    productId: string,
    productReviewsOptions: GetProductReviewOptions
  ): Promise<GetProductReviewResponse> => {
    return failingFetcher(
      `${baseUrl}/reviews/v1/products/${encodeURIComponent(productId)}?${new URLSearchParams(productReviewsOptions).toString()}`,
      {
        headers: {
          ...getHeaders({
            dynamicBackend,
            useServiceAccount: true,
          }),
        },
        next: { revalidate: REVALIDATE_ONE_HOUR_IN_SECONDS },
      }
    );
  };

  const getReviewStatisticsForProduct = (
    productId: string
  ): Promise<GetProductReviewStatisticsResponse> => {
    return failingFetcher(
      `${baseUrl}/reviews/v1/products/${encodeURIComponent(productId)}/stats`,
      {
        headers: {
          ...getHeaders({
            dynamicBackend,
            useServiceAccount: true,
          }),
        },
        cache: 'no-cache',
      }
    );
  };

  const getReviewStatisticsForProductV2 = (
    productId: string
  ): Promise<GetProductReviewStatisticsV2Response> => {
    return failingFetcher(
      `${baseUrl}/reviews/v2/products/${encodeURIComponent(productId)}/reviews/stats`,
      {
        headers: {
          ...getHeaders({
            dynamicBackend,
            useServiceAccount: true,
          }),
        },
        cache: 'no-cache',
      }
    );
  };

  const createReview = (
    productId: string,
    data: CreateReviewRequest
  ): Promise<GetReviewResponse> => {
    return failingFetcher(
      `${baseUrl}/reviews/v2/products/${encodeURIComponent(productId)}/reviews`,
      {
        method: 'POST',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
        cache: 'no-cache',
        body: JSON.stringify(data),
      }
    );
  };

  const updateReview = (
    productId: string,
    reviewId: string,
    data: UpdateReviewRequest
  ): Promise<UpdateReviewResponse> => {
    return failingFetcher(
      `${baseUrl}/reviews/v2/products/${encodeURIComponent(productId)}/reviews/${encodeURIComponent(reviewId)}`,
      {
        method: 'PATCH',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
        cache: 'no-cache',
        body: JSON.stringify(data),
      }
    );
  };

  const deleteReview = (
    productId: string,
    reviewId: string
  ): Promise<DeleteReviewResponse> => {
    return failingFetcher(
      `${baseUrl}/reviews/v2/products/${encodeURIComponent(productId)}/reviews/${encodeURIComponent(reviewId)}`,
      {
        method: 'DELETE',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const listReviews = (
    options: ListReviewsOptions
  ): Promise<ListReviewsResponse> => {
    const { productId, sort, filterRating, start, count } = options;
    const effectiveSort = sort ?? 'recent';
    const effectiveCount = count ?? 10;
    const effectiveStart = start ?? 0;

    const params = {
      sort: effectiveSort,
      ...(filterRating && { filterRating: String(filterRating) }),
      start: String(effectiveStart),
      count: String(effectiveCount),
    };

    const queryString = buildQueryString(params);

    return failingFetcher(
      `${baseUrl}/reviews/v2/products/${encodeURIComponent(productId)}/reviews?${queryString}`,
      {
        headers: getHeaders({ jwt, dynamicBackend }),
      }
    );
  };

  const getReviewActions = ({
    productId,
    reviewIds,
  }: GetReviewActionsRequest): Promise<GetReviewActionsResponse> => {
    return failingFetcher(
      `${baseUrl}/reviews/v2/products/${encodeURIComponent(productId)}/reviews/actions`,
      {
        method: 'POST',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
        cache: 'no-cache',
        body: JSON.stringify({
          ...(reviewIds && { reviewIds }),
        }),
      }
    );
  };

  const getProduct = async ({
    productId,
    locale,
    expand,
  }: GetProductRequest): Promise<GetProductResponse> => {
    const params = {
      locale,
      expand,
    };

    const queryString = buildQueryString(params);

    const url = `${baseUrl}/products/commerce/v1/products/${encodeURIComponent(productId)}${queryString ? `?${queryString}` : ''}`;

    return failingFetcher(url, {
      headers: {
        ...getHeaders({
          dynamicBackend,
          useServiceAccount: true,
        }),
      },
      cache: 'no-cache',
    });
  };

  const getProductPreview = async ({
    productId,
    locale,
    expand = [],
  }: GetProductPreviewRequest): Promise<GetProductPreviewResponse> => {
    const _params = {
      locale,
      expand,
    };

    const queryString = buildQueryString(_params);

    const url = `${baseUrl}/products/commerce/v1/products/${encodeURIComponent(productId)}/preview${queryString ? `?${queryString}` : ''}`;

    return failingFetcher(url, {
      headers: {
        ...getHeaders({
          dynamicBackend,
          jwt,
        }),
      },
      cache: 'no-cache',
    });
  };

  const getPackageVersions = async ({
    packageName,
    locale,
    status = ['PUBLISHED'],
  }: GetPackageVersionsRequest): Promise<GetPackageVersionsResponse> => {
    const params = {
      locale,
      status,
      sortBy: 'version',
      orderBy: 'DESC',
    };

    const queryString = buildQueryString(params);

    const url = `${baseUrl}/packages/v1/upm/${encodeURIComponent(packageName)}/package-versions${queryString ? `?${queryString}` : ''}`;

    return failingFetcher(url, {
      headers: {
        ...getHeaders({
          dynamicBackend,
          useServiceAccount: true,
        }),
      },
      cache: 'no-cache',
    });
  };

  const claimUpmEntitlement = (
    request: ClaimUpmEntitlementRequest
  ): Promise<ClaimUpmEntitlementResponse> => {
    return failingFetcher(`${baseUrl}/entitlements/v1/entitlements/claim/upm`, {
      method: 'POST',
      headers: {
        ...getHeaders({
          dynamicBackend,
          useServiceAccount: true,
        }),
      },
      body: JSON.stringify({ ...request, userId: genesisUser }),
    });
  };

  const checkEntitlement = (
    request: CheckEntitlementRequest
  ): Promise<CheckEntitlementResponse> => {
    return failingFetcher(
      `${baseUrl}/entitlements/v1/entitlements/check?expand=entitlement,entitlements,organizations`,
      {
        method: 'POST',
        headers: {
          ...getHeaders({
            dynamicBackend,
            useServiceAccount: true,
          }),
        },
        body: JSON.stringify({
          productId: request.productId,
          userId: genesisUser,
        }),
      }
    );
  };

  const searchProductEntitlements = async ({
    sources,
    ...queryParams
  }: SearchProductEntitlementsRequest): Promise<SearchProductsEntitlementsResponse> => {
    const params = {
      ...queryParams,
      ...(sources && { sources: sources.join(',') }),
    };

    const queryString = buildQueryString(params);

    const url = `${baseUrl}/entitlements/v1/users/${encodeURIComponent(genesisUser)}/entitlements/search${queryString ? `?${queryString}` : ''}`;

    return failingFetcher(url, {
      headers: {
        ...getHeaders({
          dynamicBackend,
          useServiceAccount: true,
        }),
      },
      cache: 'no-cache',
    });
  };

  const reportReview = async (
    productId: string,
    reviewId: string,
    reason: string,
    type: string
  ): Promise<GetReportReviewResponse> => {
    const url = `${baseUrl}/reviews/v2/products/${encodeURIComponent(productId)}/reviews/${encodeURIComponent(reviewId)}/report`;
    return failingFetcher(url, {
      method: 'POST',
      headers: {
        ...getHeaders({
          dynamicBackend,
          jwt,
        }),
      },
      cache: 'no-cache',
      body: JSON.stringify({
        reason,
        ...(type && { type: type.toLowerCase() }),
      }),
    });
  };

  const lookupProductLists = async (
    productIds: string[]
  ): Promise<LookupProductListsResponse> => {
    const queryString = buildQueryString({
      'product-ids': productIds.join(','),
    });
    return failingFetcher(
      `${baseUrl}/productlist/v2/users/${encodeURIComponent(genesisUser)}/lists/memberships${queryString ? `?${queryString}` : ''}`,
      {
        headers: {
          ...getHeaders({ dynamicBackend, jwt }),
        },
      }
    );
  };

  const lookupOrganizationProductLists = async (
    organizationId: string,
    productIds: string[]
  ): Promise<LookupProductListsResponse> => {
    const queryString = buildQueryString({
      'product-ids': productIds.join(','),
    });
    const url = `${baseUrl}/productlist/v2/organizations/${encodeURIComponent(organizationId)}/lists/memberships${queryString ? `?${queryString}` : ''}`;
    return failingFetcher(url, {
      headers: {
        ...getHeaders({ dynamicBackend, jwt }),
      },
    });
  };

  return {
    jwt,
    getAssets,
    getUserAccount,
    getNotifications,
    getUnreadNotificationsCount,
    markNotificationAsRead,
    markAllNotificationAsRead,
    getUserProfile,
    getUserPublicLists,
    getAsset,
    getPublisher,
    getPublisherLists,
    getUserLists,
    getUserList,
    getUserListItems,
    createUserList,
    updateUserList,
    deleteUserList,
    addItemToUserList,
    removeItemFromUserList,
    getListBySlug,
    getListItemsBySlug,
    getOrganizationLists,
    getOrganizationList,
    getOrganizationListItems,
    createOrganizationList,
    updateOrganizationList,
    deleteOrganizationList,
    addItemToOrganizationList,
    removeItemFromOrganizationList,
    getPublisherByOrganizationId,
    getUserReviews,
    getLoggedInUsersOrganizations,
    refreshOrganizationsCache,
    getLoggedInUsersCartItems,
    mergeDeviceCartIntoUserCart,
    getAnonymousUsersCartItem,
    getUsersEntitlements,
    searchProductEntitlements,
    createDevice,
    postNewsletterSignup,
    getSalesForAsset,
    getReviewsForProduct,
    getReviewStatisticsForProduct,
    getReviewStatisticsForProductV2,
    createReview,
    updateReview,
    deleteReview,
    listReviews,
    getReviewActions,
    getProduct,
    getProductPreview,
    getPackage: getPackageVersions,
    claimUpmEntitlement,
    checkEntitlement,
    reportReview,
    lookupProductLists,
    lookupOrganizationProductLists,
  };
}
